import {
  CAMPAIGN_CONTEXT_FLAG_LABELS,
  CAMPAIGN_CUSTOMER_KIND_LABELS,
  CAMPAIGN_EFFECT_TYPE_LABELS,
  type CampaignContextFlag,
  type CampaignCrossCondition,
  campaignCrossConditionSchema,
  POS_LINE_KIND_LABELS,
  type CampaignCustomerKind,
  type CampaignEffectType,
  type CampaignRecord,
  type CampaignScopeKind,
  type PosReport,
  type PosSettings,
  type SavePosSettings,
} from "@station/contracts";
import {
  addDays,
  Decimal,
  formatTrDate,
  istanbulDateTimeToUtc,
  type LocalDate,
  localDate,
  Money,
  toIstanbulDate,
} from "@station/core";
import {
  type BasketLine,
  campaignBuyQuantitySchema,
  campaignCustomerKindSchema,
  campaignEffectTypeSchema,
  campaignKurusSchema,
  campaignLoyaltyTierSchema,
  campaignPayQuantitySchema,
  campaignPercentSchema,
  campaignPositiveKurusSchema,
  campaignRuleIssues,
  campaignScopeKindSchema,
  campaignUnitPriceSchema,
  type ContextCustomerKind,
  LOYALTY_TIER_LIMITS,
  type SaleLineKind,
} from "@station/pricing-engine";
import type { Tone } from "@station/ui";
import { z } from "zod";
import {
  type ChartOption,
  formatChartNumber,
  GRID,
  LEGEND_POSITION,
  VALUE_AXIS_LABEL,
} from "../fuel/chart-options.js";

/**
 * Yerel kasa ekranlarının SAF dönüşümleri: kampanya durumu / etiketleri, kampanya ve ayar formlarının
 * API gövdesine çevrilmesi, kasa raporunun grafik seçeneği.
 *
 * İki kural bu dosyanın var olma nedenidir:
 *  1. **Para KURUŞ dizgesidir, ondalıklar Türkiye biçiminde girilir** ("0,5" → "0.5"); `number` hiçbir
 *     aşamada kullanılmaz (AGENTS.md §3.2). Grafiğe çizilirken (yalnızca çizim anında) TL'ye çevrilir.
 *  2. **Görünmeyen alan gövdeye ÇIKMAZ.** Etki türüne ve kapsam türüne göre ekranda olmayan alanlar
 *     temizlenir; aksi hâlde sözleşme hatası gizli bir alana düşer ve form sessizce gönderilmez
 *     (ders: `features/product-values.ts`, `features/fuel/settings-values.ts`).
 */

// ── Kampanya durumu ──────────────────────────────────────────────────────────
export type CampaignState = "ACTIVE" | "SCHEDULED" | "EXPIRED" | "INACTIVE";

export const CAMPAIGN_STATE_TONE: Record<CampaignState, Tone> = {
  ACTIVE: "success",
  SCHEDULED: "info",
  EXPIRED: "neutral",
  INACTIVE: "neutral",
};

/**
 * Kampanyanın ŞU ANKİ durumu. Kapatılan kampanya SİLİNMEZ (`isActive = false`), bu yüzden dört durum
 * ayrı ayrı okunur: kapalı → başlamamış → süresi geçmiş → yürürlükte. Geçerlilik bitişi `validTo` anı
 * DÂHİL DEĞİLDİR (motorla aynı kural: `at >= validTo` ise kampanya geçmiştir).
 */
export function campaignState(
  campaign: Pick<CampaignRecord, "isActive" | "validFrom" | "validTo">,
  now: Date = new Date(),
): CampaignState {
  if (!campaign.isActive) return "INACTIVE";
  const at = now.getTime();
  if (at < Date.parse(campaign.validFrom)) return "SCHEDULED";
  if (campaign.validTo && at >= Date.parse(campaign.validTo)) return "EXPIRED";
  return "ACTIVE";
}

/** 0 = Pazar … 6 = Cumartesi — `campaignSchema.weekdays` ile AYNI düzen. */
export const WEEKDAY_SHORT_LABELS = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"] as const;
export const WEEKDAY_LABELS = [
  "Pazar",
  "Pazartesi",
  "Salı",
  "Çarşamba",
  "Perşembe",
  "Cuma",
  "Cumartesi",
] as const;

/** Boş dizi = her gün. Sıra haftanın düzenine göre normalleştirilir (kullanıcı hangi sırayla seçerse seçsin). */
export function weekdaysText(weekdays: readonly number[], everyDay = "Her gün"): string {
  if (weekdays.length === 0 || weekdays.length === 7) return everyDay;
  return [...new Set(weekdays)]
    .sort((a, b) => a - b)
    .map((day) => WEEKDAY_SHORT_LABELS[day] ?? String(day))
    .join(", ");
}

/** "18:00 – 23:00"; tek uç verilmişse açık aralık, ikisi de boşsa gün boyu. */
export function timeWindowText(from: string | null, to: string | null, allDay = "Gün boyu"): string {
  if (!from && !to) return allDay;
  return `${from ?? "00:00"} – ${to ?? "24:00"}`;
}

/** "18.09.2026 – süresiz" / "18.09.2026 – 30.09.2026" (bitiş GÜNÜ dâhil gösterilir). */
export function validityText(
  campaign: Pick<CampaignRecord, "validFrom" | "validTo">,
  openEnd = "süresiz",
): string {
  const from = formatTrDate(new Date(campaign.validFrom));
  if (!campaign.validTo) return `${from} – ${openEnd}`;
  return `${from} – ${formatTrDate(validToDate(campaign.validTo))}`;
}

/**
 * `validTo` DIŞLAYICIDIR (`at >= validTo` → bitmiş). Ekranda ise kullanıcı "son geçerli gün"ü girer:
 * ISO an → o andan bir gün öncesinin yerel günü.
 */
export function validToDate(validToIso: string): LocalDate {
  return addDays(toIstanbulDate(new Date(validToIso)), -1);
}

/** Ters yön: "son geçerli gün (dâhil)" → ertesi günün yerel 00:00'ı, UTC ISO olarak. */
export function validToIso(lastDay: LocalDate): string {
  return istanbulDateTimeToUtc(addDays(lastDay, 1), "00:00").toISOString();
}

/**
 * Etkinin okunur karşılığı. `AMOUNT_OFF` birim BAŞINA kuruştur; `FIXED_UNIT_PRICE` satırın birim fiyat
 * ölçeğini kullanır (markette kuruş, akaryakıtta ₺/L) — bu yüzden metin "birim fiyat" der, birim koymaz.
 * `BUY_X_PAY_Y`'de etki X / Y alanlarındadır ("3 al 2 öde"); `effectValue` yalnızca okunur aynadır.
 */
export function effectText(
  campaign: Pick<CampaignRecord, "effectType" | "effectValue" | "buyQuantity" | "payQuantity">,
): string {
  const { effectValue } = campaign;
  switch (campaign.effectType) {
    case "PERCENT_OFF":
      return `%${Decimal.from(effectValue).formatTr(decimalScale(effectValue))}`;
    case "AMOUNT_OFF":
      return `${Money.fromMinor(effectValue).formatTr()} / birim`;
    case "FIXED_UNIT_PRICE":
      return `${Decimal.from(effectValue).formatTr(decimalScale(effectValue))} → birim fiyat`;
    case "BUY_X_PAY_Y":
      return campaign.buyQuantity !== null && campaign.payQuantity !== null
        ? `${campaign.buyQuantity} al ${campaign.payQuantity} öde`
        : CAMPAIGN_EFFECT_TYPE_LABELS.BUY_X_PAY_Y;
  }
}

/** Sepet eşiği: boşsa satır kampanyasıdır (`null`); doluysa "Sepet ≥ 200,00 ₺". */
export function basketThresholdText(minBasketGross: string | null, prefix = "Sepet ≥"): string | null {
  return minBasketGross === null ? null : `${prefix} ${Money.fromMinor(minBasketGross).formatTr()}`;
}

/** Listedeki etki sütunu: etki + (varsa) sepet eşiği — dışa aktarımda tek hücre. */
export function effectSummaryText(
  campaign: Pick<
    CampaignRecord,
    "effectType" | "effectValue" | "buyQuantity" | "payQuantity" | "minBasketGross"
  >,
  prefix?: string,
): string {
  const threshold = basketThresholdText(campaign.minBasketGross, prefix);
  return threshold ? `${effectText(campaign)} · ${threshold}` : effectText(campaign);
}

/** Müşteri türü; sadakat seviyesinde seviye kodları da yazılır ("Sadakat seviyesi: GOLD, PLATINUM"). */
export function customerKindText(campaign: Pick<CampaignRecord, "customerKind" | "loyaltyTiers">): string {
  const label = CAMPAIGN_CUSTOMER_KIND_LABELS[campaign.customerKind];
  return campaign.customerKind === "LOYALTY_TIER" && campaign.loyaltyTiers.length > 0
    ? `${label}: ${campaign.loyaltyTiers.join(", ")}`
    : label;
}

/**
 * Virgüllü seviye girişi → kod listesi: kırpılır, boşlar atılır, yinelenen atılır, sıra korunur. Büyük / küçük harf
 * DEĞİŞTİRİLMEZ — motor seviye kodunu birebir karşılaştırır.
 */
export function parseLoyaltyTiers(text: string): string[] {
  const tiers: string[] = [];
  for (const part of text.split(/[,;\n]/)) {
    const tier = part.trim();
    if (tier !== "" && !tiers.includes(tier)) tiers.push(tier);
  }
  return tiers;
}

export function effectTypeLabel(effectType: CampaignEffectType): string {
  return CAMPAIGN_EFFECT_TYPE_LABELS[effectType];
}

function decimalScale(value: string): number {
  return (value.split(".")[1] ?? "").replace(/0+$/, "").length;
}

// ── Türkiye biçimi ↔ API dizgesi ─────────────────────────────────────────────
/** Ayrıştırılamayan ondalık metin: sözleşmenin düzenine UYMAYAN bu işaret, hatayı ilgili alanda doğurur. */
export const INVALID_DECIMAL = "?";

export function trTextToApiDecimal(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") return "";
  try {
    return Decimal.parseTr(trimmed).toString();
  } catch {
    return INVALID_DECIMAL;
  }
}

export function apiDecimalToTrText(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  return Decimal.from(value).formatTr(decimalScale(value));
}

/** Tam sayı alanı: geçersiz metin DİZGE olarak kalır ve Zod hatası o alanın altında görünür. */
export function trTextToInt(text: string): number | string {
  const trimmed = text.trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

// ── Kampanya formu ───────────────────────────────────────────────────────────
export interface CampaignFormValues {
  code: string;
  name: string;
  /** Tam sayı metni (0–1000); küçük sayı ÖNCE uygulanır. */
  priority: string;
  scopeKind: CampaignScopeKind;
  /** Kapsam türüne göre DOLU olan alan gövdeye `scopeIds` olarak çıkar. */
  productIds: string[];
  categoryIds: string[];
  businessUnits: string[];
  /** Boş dizi = şirketin tüm istasyonları. */
  stationIds: string[];
  effectType: CampaignEffectType;
  /** `PERCENT_OFF`: Türkiye biçimi yüzde ("10", "7,5"). */
  percentValue: string;
  /** `AMOUNT_OFF`: birim başına KURUŞ. */
  amountValue: string | null;
  /** `FIXED_UNIT_PRICE`: Türkiye biçimi birim fiyat (satır ölçeğinde). */
  unitPriceValue: string;
  /** `BUY_X_PAY_Y`: tam sayı metni — her X adette… */
  buyQuantity: string;
  /** …Y adedin bedeli ödenir (1 ≤ Y < X). */
  payQuantity: string;
  /** Boş = koşul yok. */
  minQuantity: string;
  minLineGross: string | null;
  /** Sepet eşiği (kuruş). Boş = SATIR kampanyası; dolu = sepet kampanyası (ADR 0016 §17). */
  minBasketGross: string | null;
  customerKind: CampaignCustomerKind;
  /** `LOYALTY_TIER`: virgülle ayrılmış seviye kodları ("GOLD, PLATINUM"). */
  loyaltyTiers: string;
  validFrom: string;
  /** Son geçerli GÜN (dâhil); boş = süresiz. */
  validToDay: string;
  weekdays: number[];
  timeFrom: string;
  timeTo: string;
  /** Prompt 17 (ADR 0017 §23): kampanya YALNIZCA kupon kodu okutulunca uygulanır (işletmenin kendi kuponu = indirim). */
  requiresCoupon: boolean;
  /** Prompt 17 (ADR 0017 §26): çapraz koşul türü; "NONE" = koşul yok (gövdeye `null`). */
  crossKind: "NONE" | CampaignCrossCondition["kind"];
  /** `BASKET_LINE_KIND_MIN_QUANTITY`: aynı sepette bu türdeki satırların… */
  crossLineKind: SaleLineKind;
  /** …toplam miktarı en az bu kadar olmalı (Türkiye biçimi, ör. "40"). */
  crossMinQuantity: string;
  /** `CONTEXT_FLAG`: bağlamda açık olması gereken bayrak. */
  crossFlag: CampaignContextFlag;
}

export function emptyCampaignForm(today: LocalDate): CampaignFormValues {
  return {
    code: "",
    name: "",
    priority: "100",
    scopeKind: "BUSINESS_UNIT",
    productIds: [],
    categoryIds: [],
    businessUnits: ["MARKET"],
    stationIds: [],
    effectType: "PERCENT_OFF",
    percentValue: "10",
    amountValue: null,
    unitPriceValue: "",
    buyQuantity: "3",
    payQuantity: "2",
    minQuantity: "",
    minLineGross: null,
    minBasketGross: null,
    customerKind: "ANY",
    loyaltyTiers: "",
    validFrom: today,
    validToDay: "",
    weekdays: [],
    timeFrom: "",
    timeTo: "",
    requiresCoupon: false,
    crossKind: "NONE",
    crossLineKind: "FUEL",
    crossMinQuantity: "40",
    crossFlag: "EV_CHARGING_ACTIVE",
  };
}

export function campaignFormFrom(campaign: CampaignRecord): CampaignFormValues {
  const base = emptyCampaignForm(toIstanbulDate(new Date(campaign.validFrom)));
  return {
    ...base,
    code: campaign.code,
    name: campaign.name,
    priority: String(campaign.priority),
    scopeKind: campaign.scopeKind,
    productIds: campaign.scopeKind === "PRODUCT" ? [...campaign.scopeIds] : [],
    categoryIds: campaign.scopeKind === "CATEGORY" ? [...campaign.scopeIds] : [],
    businessUnits: campaign.scopeKind === "BUSINESS_UNIT" ? [...campaign.scopeIds] : [],
    stationIds: [...campaign.stationIds],
    effectType: campaign.effectType,
    percentValue: campaign.effectType === "PERCENT_OFF" ? apiDecimalToTrText(campaign.effectValue) : "",
    amountValue: campaign.effectType === "AMOUNT_OFF" ? campaign.effectValue : null,
    unitPriceValue:
      campaign.effectType === "FIXED_UNIT_PRICE" ? apiDecimalToTrText(campaign.effectValue) : "",
    buyQuantity:
      campaign.effectType === "BUY_X_PAY_Y" && campaign.buyQuantity !== null
        ? String(campaign.buyQuantity)
        : "",
    payQuantity:
      campaign.effectType === "BUY_X_PAY_Y" && campaign.payQuantity !== null
        ? String(campaign.payQuantity)
        : "",
    minQuantity: apiDecimalToTrText(campaign.minQuantity),
    minLineGross: campaign.minLineGross,
    minBasketGross: campaign.minBasketGross,
    customerKind: campaign.customerKind,
    loyaltyTiers: campaign.customerKind === "LOYALTY_TIER" ? campaign.loyaltyTiers.join(", ") : "",
    validFrom: toIstanbulDate(new Date(campaign.validFrom)),
    validToDay: campaign.validTo ? validToDate(campaign.validTo) : "",
    weekdays: [...campaign.weekdays].sort((a, b) => a - b),
    timeFrom: campaign.timeFrom ?? "",
    timeTo: campaign.timeTo ?? "",
    requiresCoupon: campaign.requiresCoupon ?? false,
    crossKind: campaign.crossCondition?.kind ?? "NONE",
    crossLineKind:
      campaign.crossCondition?.kind === "BASKET_LINE_KIND_MIN_QUANTITY"
        ? campaign.crossCondition.lineKind
        : "FUEL",
    crossMinQuantity:
      campaign.crossCondition?.kind === "BASKET_LINE_KIND_MIN_QUANTITY"
        ? apiDecimalToTrText(campaign.crossCondition.minQuantity)
        : base.crossMinQuantity,
    crossFlag:
      campaign.crossCondition?.kind === "CONTEXT_FLAG" ? campaign.crossCondition.flag : "EV_CHARGING_ACTIVE",
  };
}

/**
 * Çapraz koşul gövdesi (ADR 0017 §26): görünmeyen alt alanlar gövdeye ÇIKMAZ; "koşul yok" açıkça `null` gider (güncellemede
 * mevcut koşulu kaldırır). Miktar Türkiye biçiminden çevrilir; geçersizse DİZGE kalır ve şema hatası alana bağlanır.
 */
export function crossConditionBody(
  values: Pick<CampaignFormValues, "crossKind" | "crossLineKind" | "crossMinQuantity" | "crossFlag">,
): Record<string, unknown> | null {
  switch (values.crossKind) {
    case "NONE":
      return null;
    case "BASKET_LINE_KIND_MIN_QUANTITY":
      return {
        kind: "BASKET_LINE_KIND_MIN_QUANTITY",
        lineKind: values.crossLineKind,
        minQuantity: trTextToApiDecimal(values.crossMinQuantity),
      };
    case "CONTEXT_FLAG":
      return { kind: "CONTEXT_FLAG", flag: values.crossFlag };
  }
}

/** Listede gösterilecek çapraz koşul metni: "Aynı sepette Akaryakıt ≥ 40" / "Şarj oturumu sürüyor"; yoksa null. */
export function crossConditionText(condition: CampaignCrossCondition | null | undefined): string | null {
  if (!condition) return null;
  if (condition.kind === "CONTEXT_FLAG") return CAMPAIGN_CONTEXT_FLAG_LABELS[condition.flag];
  return `Aynı sepette ${POS_LINE_KIND_LABELS[condition.lineKind]} ≥ ${apiDecimalToTrText(condition.minQuantity)}`;
}

export function campaignScopeIds(values: CampaignFormValues): string[] {
  switch (values.scopeKind) {
    case "PRODUCT":
      return values.productIds;
    case "CATEGORY":
      return values.categoryIds;
    case "BUSINESS_UNIT":
      return values.businessUnits;
  }
}

export function campaignEffectValue(values: CampaignFormValues): string {
  switch (values.effectType) {
    case "PERCENT_OFF":
      return trTextToApiDecimal(values.percentValue);
    case "AMOUNT_OFF":
      return values.amountValue ?? "";
    case "FIXED_UNIT_PRICE":
      return trTextToApiDecimal(values.unitPriceValue);
    case "BUY_X_PAY_Y":
      // Motor kullanmaz; sunucu da "X/Y" aynasını kendisi yazar. Okunur olsun diye aynı biçimde gönderilir.
      return `${values.buyQuantity.trim()}/${values.payQuantity.trim()}`;
  }
}

/**
 * Ortak gövde alanları. `code` ve `companyId` DEĞİŞTİRİLEMEZ, bu yüzden güncelleme gövdesine girmez
 * (kampanya kimliği satış satırlarında durur).
 */
function campaignCommonBody(values: CampaignFormValues): Record<string, unknown> {
  return {
    name: values.name.trim(),
    priority: trTextToInt(values.priority),
    scopeKind: values.scopeKind,
    scopeIds: campaignScopeIds(values),
    stationIds: values.stationIds,
    effectType: values.effectType,
    effectValue: campaignEffectValue(values),
    // Görünmeyen X / Y ve seviyeler gövdeye ÇIKMAZ: tür değiştiyse açıkça boşaltılır (güncellemede de).
    buyQuantity: values.effectType === "BUY_X_PAY_Y" ? trTextToInt(values.buyQuantity) : null,
    payQuantity: values.effectType === "BUY_X_PAY_Y" ? trTextToInt(values.payQuantity) : null,
    minQuantity: values.minQuantity.trim() === "" ? null : trTextToApiDecimal(values.minQuantity),
    minLineGross: values.minLineGross === null || values.minLineGross === "" ? null : values.minLineGross,
    minBasketGross:
      values.minBasketGross === null || values.minBasketGross === "" ? null : values.minBasketGross,
    customerKind: values.customerKind,
    loyaltyTiers: values.customerKind === "LOYALTY_TIER" ? parseLoyaltyTiers(values.loyaltyTiers) : [],
    validFrom: istanbulDateTimeToUtc(localDate(values.validFrom), "00:00").toISOString(),
    validTo: values.validToDay === "" ? null : validToIso(localDate(values.validToDay)),
    weekdays: [...values.weekdays].sort((a, b) => a - b),
    timeFrom: values.timeFrom === "" ? null : values.timeFrom,
    timeTo: values.timeTo === "" ? null : values.timeTo,
    requiresCoupon: values.requiresCoupon,
    crossCondition: crossConditionBody(values),
  };
}

export function createCampaignBody(companyId: string, values: CampaignFormValues): unknown {
  return { companyId, code: values.code.trim().toLocaleUpperCase("tr-TR"), ...campaignCommonBody(values) };
}

export function updateCampaignBody(values: CampaignFormValues, version: number): unknown {
  return { ...campaignCommonBody(values), version };
}

/**
 * ÖNİZLEME sepeti: kampanyanın kapsamına göre tek satırlık örnek sepet. Motor (`computeBasket`) web'de de
 * AYNI kodla çalışsın diye gerçek `BasketLine` üretilir — kapsam ürünse ilk ürünün kimliği, kategori /
 * iş birimiyse satırın o alanı doldurulur.
 */
export function previewLineOf(
  values: CampaignFormValues,
  options: { quantity: string; unitPriceMinor: string; vatRatePercent: string },
): BasketLine {
  const scopeIds = campaignScopeIds(values);
  return {
    lineNo: 1,
    kind: "ITEM",
    productId: values.scopeKind === "PRODUCT" ? (scopeIds[0] ?? "preview-product") : "preview-product",
    categoryId: values.scopeKind === "CATEGORY" ? (scopeIds[0] ?? null) : null,
    businessUnit: values.scopeKind === "BUSINESS_UNIT" ? (scopeIds[0] ?? null) : "MARKET",
    quantity: options.quantity,
    unitPrice: options.unitPriceMinor,
    vatRateCode: "VAT_STANDARD",
    vatRatePercent: options.vatRatePercent,
  };
}

// ── Kampanya formunun doğrulaması ────────────────────────────────────────────
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Kapsam türünün form alanı: hata her zaman EKRANDAKİ alanın altında görünmelidir. */
export function scopeFieldOf(scopeKind: CampaignScopeKind): "productIds" | "categoryIds" | "businessUnits" {
  return scopeKind === "PRODUCT" ? "productIds" : scopeKind === "CATEGORY" ? "categoryIds" : "businessUnits";
}

/** Etki türünün form alanı (aynı gerekçe). X al Y öde'de hata X alanına düşer. */
export function effectFieldOf(
  effectType: CampaignEffectType,
): "percentValue" | "amountValue" | "unitPriceValue" | "buyQuantity" {
  switch (effectType) {
    case "PERCENT_OFF":
      return "percentValue";
    case "AMOUNT_OFF":
      return "amountValue";
    case "FIXED_UNIT_PRICE":
      return "unitPriceValue";
    case "BUY_X_PAY_Y":
      return "buyQuantity";
  }
}

/**
 * Önizleme bağlamındaki müşteri: kampanyanın KENDİ müşteri koşulunu sağlayan örnek müşteri (sadakat
 * seviyesinde ilk seviye). Böylece kutu "müşteri tutmadı" değil, kampanyanın etkisini gösterir.
 */
export function previewCustomerOf(campaign: {
  customerKind: CampaignCustomerKind;
  loyaltyTiers: readonly string[];
}): { customerKind: ContextCustomerKind; loyaltyTier: string | null } {
  if (campaign.customerKind === "LOYALTY_TIER")
    return { customerKind: "IDENTIFIED", loyaltyTier: campaign.loyaltyTiers[0] ?? null };
  return { customerKind: campaign.customerKind, loyaltyTier: null };
}

/** Kapsamı akaryakıt / LPG iş birimini içeren X al Y öde: litrede de çalışır ama işletme bilmelidir (uyarı). */
export function isFuelBuyXPayY(
  values: Pick<CampaignFormValues, "effectType" | "scopeKind" | "businessUnits">,
): boolean {
  return (
    values.effectType === "BUY_X_PAY_Y" &&
    values.scopeKind === "BUSINESS_UNIT" &&
    values.businessUnits.some((unit) => unit === "FUEL" || unit === "LPG")
  );
}

/**
 * Formun KENDİ alan adlarıyla doğrulaması. Kural kaynağı motorun şemasıdır (`campaignPercentSchema`,
 * `campaignKurusSchema`, `campaignUnitPriceSchema`, enum'lar) — burada yeniden YAZILMAZ, yalnızca
 * Türkiye biçimli metinden çevrildikten sonra uygulanır ve hata ekrandaki alana bağlanır.
 */
export const campaignFormSchema = z
  .object({
    code: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{1,39}$/, "Kod 2–40 karakter; harf, rakam, nokta, - ve _ içerebilir"),
    name: z.string().trim().min(1, "Kampanya adı zorunludur").max(120),
    priority: z
      .string()
      .trim()
      .regex(/^\d{1,4}$/, "Öncelik 0–1000 arası bir tam sayı olmalıdır")
      .refine((value) => Number(value) <= 1000, "Öncelik en çok 1000 olabilir"),
    scopeKind: campaignScopeKindSchema,
    productIds: z.array(z.string().min(1)),
    categoryIds: z.array(z.string().min(1)),
    businessUnits: z.array(z.string().min(1)),
    stationIds: z.array(z.string().min(1)),
    effectType: campaignEffectTypeSchema,
    percentValue: z.string(),
    amountValue: z.string().nullable(),
    unitPriceValue: z.string(),
    buyQuantity: z.string(),
    payQuantity: z.string(),
    minQuantity: z.string(),
    minLineGross: z.string().nullable(),
    minBasketGross: z.string().nullable(),
    customerKind: campaignCustomerKindSchema,
    loyaltyTiers: z.string(),
    validFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Başlangıç günü zorunludur"),
    validToDay: z.string(),
    weekdays: z.array(z.number().int().min(0).max(6)),
    timeFrom: z.string(),
    timeTo: z.string(),
    requiresCoupon: z.boolean(),
    crossKind: z.enum(["NONE", "BASKET_LINE_KIND_MIN_QUANTITY", "CONTEXT_FLAG"]),
    crossLineKind: z.enum(["FUEL", "ITEM", "WASH", "EV", "SERVICE"]),
    crossMinQuantity: z.string(),
    crossFlag: z.enum(["EV_CHARGING_ACTIVE"]),
  })
  .superRefine((values, context) => {
    const issue = (path: string, message: string) =>
      context.addIssue({ code: "custom", path: [path], message });

    if (campaignScopeIds(values).length === 0)
      issue(scopeFieldOf(values.scopeKind), "Kampanyanın kapsamı boş olamaz: en az bir kayıt seçin");

    const effectField = effectFieldOf(values.effectType);
    const effectValue = campaignEffectValue(values);
    if (values.effectType === "BUY_X_PAY_Y") {
      // Alan kuralları motorun şemasından (2 ≤ X ≤ 100, 1 ≤ Y); çapraz kural (Y < X) `campaignRuleIssues`'tan.
      const buy = intField(values.buyQuantity, campaignBuyQuantitySchema, "Alınan adedi (X) tam sayı girin");
      const pay = intField(values.payQuantity, campaignPayQuantitySchema, "Ödenen adedi (Y) tam sayı girin");
      if (typeof buy === "string") issue("buyQuantity", buy);
      if (typeof pay === "string") issue("payQuantity", pay);
      if (typeof buy === "number" && typeof pay === "number")
        for (const rule of campaignRuleIssues({
          effectType: "BUY_X_PAY_Y",
          effectValue,
          buyQuantity: buy,
          payQuantity: pay,
          customerKind: "ANY",
          loyaltyTiers: [],
        }))
          issue(rule.path, rule.message);
    } else if (effectValue === "" || effectValue === INVALID_DECIMAL)
      issue(effectField, "Geçerli bir değer girin");
    else {
      const schema =
        values.effectType === "PERCENT_OFF"
          ? campaignPercentSchema
          : values.effectType === "AMOUNT_OFF"
            ? campaignKurusSchema
            : campaignUnitPriceSchema;
      const parsed = schema.safeParse(effectValue);
      if (!parsed.success) issue(effectField, parsed.error.issues[0]?.message ?? "Geçersiz değer");
      else if (values.effectType === "PERCENT_OFF" && Number(effectValue) <= 0)
        issue(effectField, "Yüzde indirim sıfırdan büyük olmalıdır");
    }

    if (values.minQuantity.trim() !== "" && trTextToApiDecimal(values.minQuantity) === INVALID_DECIMAL)
      issue("minQuantity", "Asgari miktarı sayı olarak girin");

    if (values.minBasketGross !== null && values.minBasketGross !== "") {
      const parsed = campaignPositiveKurusSchema.safeParse(values.minBasketGross);
      if (!parsed.success) issue("minBasketGross", "Sepet eşiği sıfırdan büyük olmalıdır");
    }

    if (values.customerKind === "LOYALTY_TIER") {
      const tiers = parseLoyaltyTiers(values.loyaltyTiers);
      if (tiers.length === 0) issue("loyaltyTiers", "En az bir seviye kodu girin (ör. GOLD)");
      else if (tiers.length > LOYALTY_TIER_LIMITS.maxTiers)
        issue("loyaltyTiers", `En çok ${LOYALTY_TIER_LIMITS.maxTiers} seviye girilebilir`);
      else if (tiers.some((tier) => !campaignLoyaltyTierSchema.safeParse(tier).success))
        issue("loyaltyTiers", `Seviye kodu en çok ${LOYALTY_TIER_LIMITS.maxLength} karakter olabilir`);
    }

    if (values.validToDay !== "" && values.validToDay < values.validFrom)
      issue("validToDay", "Bitiş günü başlangıçtan önce olamaz");

    for (const field of ["timeFrom", "timeTo"] as const)
      if (values[field] !== "" && !TIME_PATTERN.test(values[field])) issue(field, "Saati SS:DD girin");

    // Çapraz koşul: kural kaynağı motorun şemasıdır (`campaignCrossConditionSchema`) — burada yeniden yazılmaz.
    const cross = crossConditionBody(values);
    if (cross !== null) {
      const parsed = campaignCrossConditionSchema.safeParse(cross);
      if (!parsed.success)
        issue(
          "crossMinQuantity",
          values.crossMinQuantity.trim() === "" || cross.minQuantity === INVALID_DECIMAL
            ? "Asgari miktarı sayı olarak girin"
            : (parsed.error.issues[0]?.message ?? "Geçersiz çapraz koşul"),
        );
    }
  });
export type CampaignFormOutput = z.output<typeof campaignFormSchema>;

/** Tam sayı alanı: sayıya çevrilir ve motorun şemasıyla sınanır; hata varsa Türkçe İLETİ döner. */
function intField(text: string, schema: z.ZodType<number>, invalid: string): number | string {
  const value = trTextToInt(text);
  if (typeof value !== "number") return invalid;
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : (parsed.error.issues[0]?.message ?? invalid);
}

// ── Kasa ayarları formu ──────────────────────────────────────────────────────
export interface PosSettingsFormValues {
  companyId: string;
  /** null → şirket satırı; dolu → o istasyonun geçersiz kılması. */
  stationId: string | null;
  version: number;
  fiscalRequired: boolean;
  /** Kuruş. */
  offlineCreditTolerance: string | null;
  /** Dakika, tam sayı metni (0 = kapalı). */
  pendingFuelAutoSettleMinutes: string;
  ageRestrictedConfirmation: boolean;
  receiptFooter: string;
  needsVerification: boolean;
}

export function posSettingsFormFrom(
  settings: PosSettings,
  overrides: { stationId?: string | null; version?: number } = {},
): PosSettingsFormValues {
  return {
    companyId: settings.companyId,
    stationId: overrides.stationId === undefined ? settings.stationId : overrides.stationId,
    version: overrides.version === undefined ? settings.version : overrides.version,
    fiscalRequired: settings.fiscalRequired,
    offlineCreditTolerance: settings.offlineCreditTolerance,
    pendingFuelAutoSettleMinutes: String(settings.pendingFuelAutoSettleMinutes),
    ageRestrictedConfirmation: settings.ageRestrictedConfirmation,
    receiptFooter: settings.receiptFooter ?? "",
    needsVerification: settings.needsVerification,
  };
}

/** PUT gövdesi: TAMAMI değişir (kısmi güncelleme yoktur). Boş fiş alt yazısı `null` gider. */
export function posSettingsBody(values: PosSettingsFormValues): unknown {
  const footer = values.receiptFooter.trim();
  return {
    companyId: values.companyId,
    stationId: values.stationId,
    fiscalRequired: values.fiscalRequired,
    offlineCreditTolerance: values.offlineCreditTolerance ?? "0",
    pendingFuelAutoSettleMinutes: trTextToInt(values.pendingFuelAutoSettleMinutes),
    ageRestrictedConfirmation: values.ageRestrictedConfirmation,
    receiptFooter: footer === "" ? null : footer,
    needsVerification: values.needsVerification,
    version: values.version,
  } satisfies Record<keyof SavePosSettings | string, unknown>;
}

// ── Kasa raporu ──────────────────────────────────────────────────────────────
export interface PosReportChartLabels {
  net: string;
  discount: string;
  amountAxis: string;
}

/** Kuruş → TL (yalnızca ÇİZİM için; hesap değildir — AGENTS.md §3.2). */
function toMajor(minor: string): number {
  return Number(Money.fromMinor(minor).toMajorString());
}

/**
 * Günlük net satış + indirim sütun grafiği. Rapor satırları gün × istasyon × kasiyer kırılımındadır;
 * grafik GÜNE toplar (kasiyer kırılımı zaten tablodadır ve grafiği okunmaz hâle getirirdi).
 */
export function posReportChartOption(
  rows: readonly PosReport["rows"][number][],
  labels: PosReportChartLabels,
): ChartOption {
  const byDay = new Map<string, { net: bigint; discount: bigint }>();
  for (const row of rows) {
    const current = byDay.get(row.businessDate) ?? { net: 0n, discount: 0n };
    byDay.set(row.businessDate, {
      net: current.net + BigInt(row.net),
      discount: current.discount + BigInt(row.discountTotal),
    });
  }
  const days = [...byDay.keys()].sort();
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.net, labels.discount] },
    xAxis: {
      type: "category",
      data: days.map((day) => formatTrDate(day as LocalDate)),
      axisLabel: { hideOverlap: true },
    },
    yAxis: { type: "value", name: labels.amountAxis, axisLabel: VALUE_AXIS_LABEL },
    series: [
      { type: "bar", name: labels.net, data: days.map((day) => toMajor(String(byDay.get(day)!.net))) },
      {
        type: "bar",
        name: labels.discount,
        data: days.map((day) => toMajor(String(byDay.get(day)!.discount))),
      },
    ],
  };
}

/** Rapor satırının tahsilat kırılımını "Nakit 1.200,00 ₺ · Kart 800,00 ₺" biçiminde okunur yapar. */
export function tenderTotalsText(
  tenders: readonly { tenderType: string; amount: string }[],
  label: (code: string) => string,
): string {
  if (tenders.length === 0) return "—";
  return tenders
    .map((tender) => `${label(tender.tenderType)} ${Money.fromMinor(tender.amount).formatTr()}`)
    .join(" · ");
}
